import { z } from 'zod';

// The Bank (as Lagos Life's 🏦 Bank and its Naija Reserve Bank, worked out from its public client code): the
// wallet's balance and all your money; savings that earn interest by the minute, paid into savings once a day
// on balances up to a cap; fixed-term bonds that lock money and pay back more when they mature (cashed in to
// the wallet); the week's bills in one place; and the money history, every penny in and out with the reason.
// The rates and limits are content the admins edit (Content → Bank).

const pence = (max: number) => z.number().int().min(0).max(max);
// basis points: 50 is 0.5%
const bps = z.number().int().min(0).max(5_000);

export const BankContent = z.object({
  visible: z.boolean(),
  // the bank's name, as Lagos's "Naija Reserve Bank"
  name: z.string().trim().min(2).max(40),
  short: z.string().trim().min(2).max(20),
  // savings interest a week, and on how much of the savings at most
  savingsBps: bps,
  interestCapPence: pence(100_000_000_000),
  // a bond: what it pays back over its term, the term, the smallest bond, at most this many open and this much
  bondBps: bps,
  bondDays: z.number().int().min(1).max(90),
  minBondPence: pence(1_000_000_000),
  maxBonds: z.number().int().min(1).max(50),
  bondMaxPence: pence(100_000_000_000),
  // the smallest deposit or withdrawal, and how many of each (and bonds) a day
  minMovePence: pence(1_000_000_000),
  movesPerDay: z.number().int().min(1).max(200),
  bondsPerDay: z.number().int().min(1).max(100),
});
export type BankContent = z.infer<typeof BankContent>;

// Lagos's numbers at its ₦1,000 to our £1: ₦1,000 smallest move, ₦10,000 smallest bond, ₦1bn interest cap,
// ₦500m in bonds, 0.5% a week savings, 2% bonds over 7 days, 20 moves and 10 bonds a day
export const DEFAULT_BANK: BankContent = {
  visible: true,
  name: 'Threadneedle Reserve Bank',
  short: 'the Reserve',
  savingsBps: 50,
  interestCapPence: 100_000_000,
  bondBps: 200,
  bondDays: 7,
  minBondPence: 1_000,
  maxBonds: 10,
  bondMaxPence: 50_000_000,
  minMovePence: 100,
  movesPerDay: 20,
  bondsPerDay: 10,
};
export const bankOf = (content: { bank?: BankContent }) => content.bank ?? DEFAULT_BANK;
export function bankProblems(bank: BankContent): string[] {
  const problems: string[] = [];
  if (bank.minBondPence > bank.bondMaxPence) problems.push('The smallest bond is more than the most in bonds.');
  return problems;
}

export const WEEK_MS = 7 * 24 * 60 * 60 * 1000;
export const DAY_MS = 24 * 60 * 60 * 1000;
export const pct = (value: number) => `${+(value / 100).toFixed(2)}%`;
// a bond's payout: its principal and the term's interest (whole pence, rounded down)
export const bondPayout = (principal: number, rateBps: number) =>
  principal + Math.floor((principal * rateBps) / 10_000);
// interest earned on savings between two moments (fractions of a penny), on the capped balance
export const interestBetween = (savedPence: number, fromMs: number, toMs: number, bank: BankContent) =>
  toMs > fromMs && savedPence > 0
    ? (Math.min(savedPence, bank.interestCapPence) * bank.savingsBps * (toMs - fromMs)) / 10_000 / WEEK_MS
    : 0;
// about how much a week's interest comes to now
export const weekInterest = (savedPence: number, bank: BankContent) =>
  Math.floor((Math.min(savedPence, bank.interestCapPence) * bank.savingsBps) / 10_000);

// ---------- over the wire ----------

export const BondView = z.object({
  id: z.string().uuid(),
  principalPence: z.number().int(),
  payoutPence: z.number().int(),
  rateBps: z.number().int(),
  boughtAt: z.string(),
  maturesAt: z.string(),
  status: z.enum(['open', 'paid', 'closed']),
  paidAt: z.string().nullable(),
});
export type BondView = z.infer<typeof BondView>;

export const BankView = z.object({
  walletPence: z.number().int(),
  savedPence: z.number().int(),
  bondsPence: z.number().int(),
  // every account of this life: the wallet, savings, bonds and the businesses' tills
  netWorthPence: z.number().int(),
  // interest so far (paid) and building up now (not yet paid), and when it is paid next
  earnedPence: z.number().int(),
  pendingPence: z.number().int(),
  nextPaidAt: z.string(),
  bonds: z.array(BondView),
  // what is left for today
  movesLeft: z.number().int(),
  bondsLeft: z.number().int(),
  // the server's time, so the screen counts down from it
  at: z.string(),
});
export type BankView = z.infer<typeof BankView>;
export const BankReply = z.object({ bank: BankView, message: z.string().optional() });
export type BankReply = z.infer<typeof BankReply>;

export const SavingsRequest = z.object({
  action: z.enum(['in', 'out']),
  pence: z.number().int().positive().max(9_000_000_000_000_000),
});
export type SavingsRequest = z.infer<typeof SavingsRequest>;
export const BondRequest = z.object({ pence: z.number().int().positive().max(9_000_000_000_000_000) });
export type BondRequest = z.infer<typeof BondRequest>;

// the money history: the wallet's movements, newest first
export const MoneyLine = z.object({
  id: z.number().int(),
  kind: z.string(),
  label: z.string(),
  amountPence: z.number().int(),
  balanceAfterPence: z.number().int(),
  at: z.string(),
  // who it was with, or a note, where there is one
  detail: z.string().nullable(),
});
export type MoneyLine = z.infer<typeof MoneyLine>;
export const MoneyHistoryReply = z.object({ lines: z.array(MoneyLine), more: z.boolean() });
export type MoneyHistoryReply = z.infer<typeof MoneyHistoryReply>;
